/**
 * Application analytics events, pushed to the Google Tag Manager data layer.
 *
 * GA4 is deployed through the GTM container (GTM-PBZGGTRZ), not by a gtag.js
 * snippet in index.html. That distinction decides the shape of this file: when GA4
 * is loaded by a container, `gtag('event', ...)` is not the supported way to send
 * it an event. A gtag() call pushes an `arguments` object onto the data layer,
 * which GTM's Custom Event trigger cannot match on, so those events would leave
 * the page and reach nothing. The supported form is a plain object with an `event`
 * key, which is what this pushes.
 *
 * Parameter names are unchanged from the gtag era (transaction_id, value,
 * currency, method, page_path), so the GA4 event parameters keep reporting under
 * the names the property and the linked Ads account already know.
 *
 * Container side, this needs a Custom Event trigger matching these event names, a
 * GA4 Event tag using {{Event}} as the event name, and — the part that is easy to
 * miss — a Data Layer Variable mapped for each parameter above under that tag's
 * Event Parameters. A GA4 Event tag does not sweep up arbitrary keys from the data
 * layer, so without those mappings `purchase` reaches GA4 with no value, currency
 * or transaction_id while the payload here still looks perfectly correct. See
 * docs/internal-guides/technical/GOOGLE_ADS_CONVERSION_TRACKING.md.
 *
 * Consent is not checked here. client/public/consent.js holds the container itself
 * until the visitor consents, and Consent Mode defaults every storage type to
 * denied before anything loads, so a push made pre-consent simply sits in the
 * array unread.
 */

const push = (payload) => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

export const trackEvent = (name, params = {}) => {
  if (!name) return;
  push({ event: name, ...params });
};

/**
 * Sent on every route change, including the first.
 *
 * The GA4 configuration tag in the container must therefore have "Send a page view
 * event when this configuration loads" turned OFF — this is the `send_page_view:
 * false` the direct tag used to carry. With it on, the first load of any URL counts
 * twice: once from the container and once from here.
 */
export const trackPageView = (path) => {
  trackEvent('page_view', { page_path: path });
};
